import React, { useCallback, useEffect, useState } from 'react'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material'
import { PromptSelect } from 'components/tree/AITreeTool/PromptSelect'
import { selectedPromptAtom } from 'components/tree/AITreeTool/atoms'
import { usePromptSelect } from 'components/tree/AITreeTool/hooks'
import {
  TreeIdInput,
  type TreeSelectResult,
} from 'components/tree/navigationHelpers/TreeNodeRouter'
import { useBatchRegister } from './hooks'

type RegisterBatchProps = {
  onSuccess?: () => void
}

export function RegisterBatch({ onSuccess }: RegisterBatchProps) {
  const [selectedTree, setSelectedTree] = useState<TreeSelectResult | null>(
    null,
  )
  const [treeFinderKey, setTreeFinderKey] = useState(0)
  const { selectedPrompt } = usePromptSelect()
  const setSelectedPrompt = useSetAtom(selectedPromptAtom)

  useEffect(() => {
    setSelectedPrompt(null)
  }, [setSelectedPrompt])

  const handleSuccess = React.useCallback(() => {
    onSuccess?.()
    setSelectedTree(null)
    setSelectedPrompt(null)
    setTreeFinderKey((k) => k + 1)
  }, [onSuccess, setSelectedPrompt])
  const { register, isLoading, error } = useBatchRegister(handleSuccess)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (selectedPrompt && selectedTree) {
        register({
          tree_id: selectedTree.treeId,
          tree_ver: selectedTree.treeVer,
          area: selectedPrompt.area,
          prompt_id: selectedPrompt.promptId,
          title: selectedTree.title,
        })
      }
    },
    [selectedPrompt, selectedTree, register],
  )

  const isValid = selectedTree !== null && selectedPrompt !== null

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Register Tree In Batch Jobs
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TreeIdInput
            key={treeFinderKey}
            onSelect={setSelectedTree}
            fetchTrigger="button"
            showErrors
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Prompt
            </Typography>
            <PromptSelect />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isLoading ? 'Registering...' : 'Register'}
          </Button>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </form>
    </Paper>
  )
}
